import React from 'react'

import { ChartSegmentSelectionScope, TotalSampleTag } from '../components/chart-segment/selection-scope'
import { SlideFootnote } from './SlideFootnote'
import { SlideFrame } from './SlideFrame'
import { SlideHeaderChrome } from './SlideHeaderChrome'
import { presetSlot } from './presetSlots'

/**
 * The `band` layout (plots plan Stage 2, AE): a text band of two columns (`lead` and
 * `aside`) under the header, over a wide plot. The band's geometry comes from the
 * preset's bound slots (`layouts/band.json` plus the preset's overrides); the slide
 * fills the two columns and draws its plot panel at the bound `plot` slot.
 */
export interface BandLayoutProps {
  /** The slide type, which names its bound slots. */
  preset: string
  eyebrow: string
  clientLogoUrl: string | null
  testIds: { layout: string; band: string }
  /** The band's two columns, in order: lead, then aside. */
  band: React.ReactNode
  /** The plot panel, drawn at the preset's bound `plot` slot. */
  children: React.ReactNode
  /** The slide's footer (its base, a split's small groups), bottom right. */
  footerText?: string | null
}

export function BandLayout(props: BandLayoutProps) {
  const { preset, eyebrow, clientLogoUrl, testIds, band, children, footerText } = props
  const lead = presetSlot(preset, 'lead').rect!
  const aside = presetSlot(preset, 'aside').rect!
  return (
    <ChartSegmentSelectionScope>
      <SlideFrame fullBleed>
        <div data-testid={testIds.layout} style={{ position: 'absolute', inset: 0, overflow: 'hidden', background: 'var(--ds-slide-bg)' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0 }}>
            <SlideHeaderChrome eyebrowText={eyebrow} clientLogoUrl={clientLogoUrl} eyebrowTextTransform="none" />
          </div>
          <section
            data-testid={testIds.band}
            style={{
              position: 'absolute',
              left: lead.x,
              top: lead.y,
              width: aside.x + aside.w - lead.x,
              height: lead.h,
              display: 'grid',
              gridTemplateColumns: `${lead.w}px ${aside.w}px`,
              columnGap: aside.x - lead.x - lead.w,
            }}
          >
            {band}
            <TotalSampleTag style={{ position: 'absolute', bottom: 0, right: 0 }} />
          </section>
          {children}
          <SlideFootnote text={footerText} />
        </div>
      </SlideFrame>
    </ChartSegmentSelectionScope>
  )
}
